import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { GridPaginationModel } from '@mui/x-data-grid';

import { PaginationQuery } from '@libs/dto';

import { DEFAULT_PAGINATION_MODEL, DEFAULT_PAGINATION_QUERY } from './DataGrid.const';

export interface DataGridProviderProps<T> {
  query: any;
  id?: number;
  children?: ReactNode;
}

export interface DataGridContextProps<T> {
  rows: T[];
  rowCount: number;
  loading: boolean;
  paginationModel: GridPaginationModel;
  queryParams: PaginationQuery;
  query: any;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  setQueryParams: Dispatch<SetStateAction<PaginationQuery>>;
}

export const DataGridContext = createContext<DataGridContextProps<any> | undefined>(undefined);

export const DataGridProvider = <T,>({ query, id, children }: DataGridProviderProps<T>) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(DEFAULT_PAGINATION_MODEL);
  const [queryParams, setQueryParams] = useState<PaginationQuery>({ ...DEFAULT_PAGINATION_QUERY, id: id });

  const { data, isFetching } = query(queryParams);

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
      setQueryParams({ ...queryParams, id: id, page: model.page + 1, limit: model.pageSize });
    },
    [queryParams],
  );

  const value = useMemo(
    () => ({
      id,
      paginationModel,
      queryParams,
      query,
      onPaginationModelChange,
      setQueryParams,
      rows: data?.items ?? [],
      rowCount: data?.meta?.totalItems ?? 0,
      loading: isFetching,
    }),
    [data, isFetching, paginationModel, queryParams, query, id, onPaginationModelChange, setQueryParams],
  );

  return <DataGridContext.Provider value={value}>{children}</DataGridContext.Provider>;
};

export function useDataGrid<T>() {
  return useContext(DataGridContext) as DataGridContextProps<T>;
}
